<template>
  <div :class="ns.b('panel')">
    <p :class="ns.be('panel', 'header')">
      <jd-checkbox
        v-model="allChecked"
        :indeterminate="isIndeterminate"
        :validate-event="false"
        @change="handleAllCheckedChange"
      >
        {{ title }}
      </jd-checkbox>
      <span :class="ns.bem('panel', 'header', 'summary')">{{
        checkedSummary
      }}</span>
    </p>

    <div :class="[ns.be('panel', 'body'), ns.is('with-footer', hasFooter)]">
      <jd-input
        v-if="filterable"
        v-model="query"
        :class="ns.be('panel', 'filter')"
        size="small"
        :placeholder="placeholder"
        :prefix-icon="Search"
        clearable
        :validate-event="false"
      />
      <jd-checkbox-group
        v-show="!hasNoMatch && !isEmpty(data)"
        v-model="checked"
        :validate-event="false"
        :class="[ns.is('filterable', filterable), ns.be('panel', 'list')]"
      >
        <jd-checkbox
          v-for="item in filteredData"
          :key="item[propsAlias.key]"
          :class="ns.be('panel', 'item')"
          :label="item[propsAlias.key]"
          :disabled="item[propsAlias.disabled]"
          :validate-event="false"
        >
          <option-content :option="optionRender?.(item)" />
        </jd-checkbox>
      </jd-checkbox-group>
      <div
        v-show="hasNoMatch || isEmpty(data)"
        :class="ns.be('panel', 'empty')"
      >
        <jd-empty
          image=" "
          :description="
            hasNoMatch ? t('el.transfer.noMatch') : t('el.transfer.noData')
          "
        />
      </div>
    </div>
    <p v-if="hasFooter" :class="ns.be('panel', 'footer')">
      <slot />
    </p>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, toRefs, useSlots } from 'vue'
import { isEmpty } from '@jdesign-vue/utils'
import { useLocale, useNamespace } from '@jdesign-vue/hooks'
import { JdCheckbox, JdCheckboxGroup } from '@jdesign-vue/components/checkbox'
import { JdInput } from '@jdesign-vue/components/input'
import JdEmpty from '@jdesign-vue/components/empty'
import { Search } from '@jd/jdesign-icons-vue'
import { transferPanelEmits, transferPanelProps } from './transfer-panel'
import { useCheck, usePropsAlias } from './composables'

import type { VNode } from 'vue'
import type { TransferPanelState } from './transfer-panel'

defineOptions({
  name: 'JdTransferPanel',
})

const props = defineProps(transferPanelProps)
const emit = defineEmits(transferPanelEmits)
const slots = useSlots()

const OptionContent = ({ option }: { option: VNode | VNode[] }) => option

const { t } = useLocale()
const ns = useNamespace('transfer')

const panelState = reactive<TransferPanelState>({
  checked: [],
  allChecked: false,
  query: '',
  checkChangeByUser: true,
})

const propsAlias = usePropsAlias(props)

const {
  filteredData,
  checkedSummary,
  isIndeterminate,
  handleAllCheckedChange,
} = useCheck(props, panelState, emit)

const hasNoMatch = computed(
  () => !isEmpty(panelState.query) && isEmpty(filteredData.value)
)

const hasFooter = computed(() => !isEmpty(slots.default!()[0].children))

const { checked, allChecked, query } = toRefs(panelState)

defineExpose({
  /** @description filter keyword */
  query,
})
</script>
